import { useEffect, useState } from "react";
import { slugify } from "../data/artists";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Curate({ artists }) {
  const [key, setKey] = useState(() => sessionStorage.getItem("wowow-curate-key") || "");
  const [target, setTarget] = useState("");
  const [image, setImage] = useState(null);
  const [video, setVideo] = useState(null);
  const [status, setStatus] = useState(null);
  const [existing, setExisting] = useState([]);

  const options = artists.flatMap((a) => {
    const slug = slugify(a.name);
    const opts = [{ value: slug, label: a.name }];
    (a.periods || []).forEach((p) => opts.push({ value: `${slug}-${p.slug}`, label: `${a.name} — ${p.era}` }));
    (a.pieces || []).forEach((p, i) => opts.push({ value: `${slug}-piece-${i + 1}`, label: `${a.name} — ${p.title}` }));
    opts.push({ value: `${slug}-photo-1`, label: `${a.name} — studio photo 1` });
    opts.push({ value: `${slug}-photo-2`, label: `${a.name} — studio photo 2` });
    return opts;
  });

  useEffect(() => {
    fetch(`${API}/api/artwork`)
      .then((r) => r.json())
      .then((d) => setExisting(d.items || []))
      .catch(() => {});
  }, [status]);

  const upload = async (e) => {
    e.preventDefault();
    if (!key || !target || (!image && !video)) {
      setStatus({ error: "Add the review key, choose a world, and attach at least one file." });
      return;
    }
    setStatus({ busy: true });
    const fd = new FormData();
    fd.append("key", key);
    fd.append("slug", target);
    if (image) fd.append("image", image);
    if (video) fd.append("video", video);
    try {
      const res = await fetch(`${API}/api/artwork`, { method: "POST", body: fd });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
      sessionStorage.setItem("wowow-curate-key", key);
      setStatus({ ok: `Placed in ${target}. The frame updates on the next page load.` });
      setImage(null);
      setVideo(null);
    } catch (err) {
      setStatus({ error: String(err.message || err) });
    }
  };

  return (
    <section className="curate" data-testid="curate-page">
      <div className="curate-inner">
        <span className="kicker">The WOWOW Gallery</span>
        <h1 className="display">Curate the frames</h1>
        <p className="curate-sub">
          Upload the still (JPG) and the motion (MP4) for a piece — the film plays in the frame, the still stands
          behind it.
        </p>
        <form onSubmit={upload} className="curate-form">
          <label>
            Review key
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Your review key"
              data-testid="curate-key-input"
            />
          </label>
          <label>
            World / piece
            <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="curate-artist-select">
              <option value="">Choose…</option>
              {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label>
            Still image — JPG
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setImage(e.target.files[0] || null)}
              data-testid="curate-image-input"
            />
          </label>
          <label>
            Motion — MP4
            <input
              type="file"
              accept="video/mp4"
              onChange={(e) => setVideo(e.target.files[0] || null)}
              data-testid="curate-video-input"
            />
          </label>
          <button type="submit" className="view-piece-btn" data-testid="curate-upload-button" disabled={status?.busy}>
            {status?.busy ? "Placing…" : "Place in the frame"}
          </button>
          {status?.ok && <p className="curate-ok" data-testid="curate-status">{status.ok}</p>}
          {status?.error && <p className="curate-err" data-testid="curate-status">{status.error}</p>}
        </form>
        {existing.length > 0 && (
          <div className="curate-existing">
            <div className="eyebrow">In the frames now</div>
            {existing.map((it) => (
              <div className="spec-row" key={it.slug}>
                <span>{it.slug}</span>
                <span>{[it.image && "still", it.video && "film"].filter(Boolean).join(" + ")}</span>
              </div>
            ))}
          </div>
        )}
        <a href="#hall" className="curate-back" data-testid="curate-back-link">← Back to the Hall</a>
      </div>
    </section>
  );
}
